/**
 * GstStep — rate-wise GST ledger creation, a terminal step of AccountFormWizard
 * reached by picking "GST Ledger" instead of an account type. Collects
 * component + direction + rate and derives everything else (code, name,
 * account type/subtype, parent) from the org's existing consolidated GST
 * ledger, mirroring what `resolveTaxAccount` does automatically the first
 * time an invoice posts at that rate — so a manually-created ledger is
 * indistinguishable from an auto-created one.
 *
 * Code convention matches party subledgers: "{consolidatedCode}-{rate}",
 * e.g. "2330-9" for CGST Output @9%, "1200-C001" for a customer receivable.
 */

import { useEffect } from 'react'

import { FormField, Select, NumericInput } from '@elsapiens/ui'
import { AlertTriangle } from 'lucide-react'

import { type AccountFormData } from './chart-of-accounts-helpers'
import { RESERVED_CODES, type ReservedAccountCode } from '../../constants/reserved-account-codes'

import type { Account } from '../../services/account-service'

const GST_COMPONENT_OPTIONS = [
  { value: 'cgst', label: 'CGST' },
  { value: 'sgst', label: 'SGST' },
  { value: 'igst', label: 'IGST' },
]

const GST_DIRECTION_OPTIONS = [
  { value: 'output', label: 'Output — collected on sales (liability)' },
  { value: 'input', label: 'Input / ITC — paid on purchases (asset)' },
]

const QUICK_RATES = [0.25, 3, 5, 12, 18, 28]

// ── Naming / code helpers (mirror server/internal/service/gst_ledger.go) ────────

function formatGstRate(rate: number): string {
  return String(Math.round(rate * 100) / 100)
}

function gstLedgerName(component: string, direction: string, rate: number): string {
  const dir = direction === 'input' ? 'Input' : 'Output'
  return `${component.toUpperCase()} ${dir} @${formatGstRate(rate)}%`
}

function gstRateLeafCode(baseCode: string, rate: number): string {
  return `${baseCode}-${formatGstRate(rate)}`
}

export function resolveGstConsolidated(component: string, direction: string): ReservedAccountCode | undefined {
  if (!component || !direction) { return undefined }
  const tag = `${component}_${direction}`
  return Object.values(RESERVED_CODES).find(r => r.systemTag === tag)
}

// ── Resolution + auto-fill ───────────────────────────────────────────────────

interface GstResolution {
  consolidated: ReservedAccountCode | undefined
  consolidatedAccount: Account | undefined
  parentAccount: Account | undefined
  duplicateAccount: Account | undefined
  rate: number
  hasValidRate: boolean
}

function resolveGst(formData: AccountFormData, accountsMap: Map<string, Account>): GstResolution {
  const consolidated = resolveGstConsolidated(formData.gstComponent, formData.gstDirection)
  const accounts = Array.from(accountsMap.values())
  const consolidatedAccount = consolidated ? accounts.find(a => a.code === consolidated.code) : undefined
  const parentAccount = consolidated ? accounts.find(a => a.code === consolidated.parentCode) : undefined
  const rate = parseFloat(formData.gstRate)
  const hasValidRate = !Number.isNaN(rate) && rate > 0
  const duplicateAccount = (consolidated && hasValidRate)
    ? accounts.find(a => a.gstComponent === formData.gstComponent && a.gstDirection === formData.gstDirection && a.gstRate === rate)
    : undefined
  return { consolidated, consolidatedAccount, parentAccount, duplicateAccount, rate, hasValidRate }
}

function useGstAutoFill(
  formData: AccountFormData,
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>,
  resolution: GstResolution,
): void {
  const { consolidated, consolidatedAccount, parentAccount, duplicateAccount, rate, hasValidRate } = resolution
  useEffect(() => {
    if (!consolidated || !consolidatedAccount || !hasValidRate || duplicateAccount) { return }
    const code = gstRateLeafCode(consolidated.code, rate)
    const name = gstLedgerName(formData.gstComponent, formData.gstDirection, rate)
    const parentId = parentAccount?.id ?? ''
    setFormData(prev => (prev.code === code && prev.name === name && prev.parentId === parentId)
      ? prev
      : { ...prev, code, name, parentId, accountType: consolidated.accountType, accountSubtype: consolidated.accountSubtype })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolution fields cover the real deps; formData/setFormData are stable-shaped
  }, [consolidated, consolidatedAccount, parentAccount, duplicateAccount, rate, hasValidRate, formData.gstComponent, formData.gstDirection])
}

// ── Fields ────────────────────────────────────────────────────────────────────

function GstComponentFields({ formData, setFormData }: {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
}): JSX.Element {
  return (
    <div className="grid grid-cols-2 el-gap-field">
      <FormField required label="GST Component">
        <Select
          options={GST_COMPONENT_OPTIONS}
          value={formData.gstComponent}
          onChange={value => setFormData(prev => ({ ...prev, gstComponent: value, gstDirection: prev.gstDirection || 'output' }))}
        />
      </FormField>
      <FormField required label="Direction">
        <Select
          options={GST_DIRECTION_OPTIONS}
          value={formData.gstDirection}
          onChange={value => setFormData(prev => ({ ...prev, gstDirection: value }))}
        />
      </FormField>
    </div>
  )
}

function GstRateField({ formData, setFormData }: {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
}): JSX.Element {
  return (
    <FormField required helperText="e.g. 9 for a 9% CGST Output ledger" label="GST rate %">
      <div className="flex flex-col el-gap-xs">
        <NumericInput
          allowNegative={false}
          max={50}
          min={0}
          precision={2}
          suffix="%"
          value={formData.gstRate}
          onChange={v => setFormData(prev => ({ ...prev, gstRate: v !== null ? String(v) : '' }))}
        />
        <div className="flex flex-wrap gap-1.5">
          {QUICK_RATES.map(r => (
            <button
              key={r}
              className="px-2 py-0.5 rounded text-xs font-medium border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, gstRate: String(r) }))}
            >
              {r}%
            </button>
          ))}
        </div>
      </div>
    </FormField>
  )
}

function GstWarning({ children, testId }: { children: React.ReactNode; testId: string }): JSX.Element {
  return (
    <div className="el-edge flex items-start gap-2.5 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30" data-testid={testId}>
      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
      <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">{children}</p>
    </div>
  )
}

function GstResolutionPreview({ formData, resolution }: { formData: AccountFormData; resolution: GstResolution }): JSX.Element | null {
  const { consolidated, consolidatedAccount, parentAccount, duplicateAccount, hasValidRate } = resolution
  if (!formData.gstComponent) { return null }
  if (!consolidated || !consolidatedAccount) {
    return (
      <GstWarning testId="gst-missing-consolidated">
        The consolidated {formData.gstComponent.toUpperCase()} {formData.gstDirection || 'output'} ledger doesn&apos;t
        exist in this org yet. Create it first via Chart of Accounts, then come back to add a rate-wise ledger under it.
      </GstWarning>
    )
  }
  if (!hasValidRate) {
    return <p className="text-xs text-muted-foreground">Enter a rate to preview the ledger that will be created.</p>
  }
  if (duplicateAccount) {
    return (
      <GstWarning testId="gst-duplicate-warning">
        A ledger for this exact rate already exists: <span className="font-mono font-semibold">{duplicateAccount.code}</span> — {duplicateAccount.name}.
      </GstWarning>
    )
  }
  return (
    <div className="rounded bg-muted/50 el-p-xs text-xs text-muted-foreground" data-testid="gst-preview">
      <span className="font-medium text-foreground">Will create: </span>
      <span className="font-mono font-semibold" data-testid="gst-preview-code">{formData.code}</span> — {formData.name}
      {parentAccount && <> under <span className="font-mono">{parentAccount.code} {parentAccount.name}</span></>}
    </div>
  )
}

// ── GstStep ───────────────────────────────────────────────────────────────────

export interface GstStepProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  accountsMap: Map<string, Account>
}

export function GstStep({ formData, setFormData, accountsMap }: GstStepProps): JSX.Element {
  const resolution = resolveGst(formData, accountsMap)
  useGstAutoFill(formData, setFormData, resolution)

  return (
    <div className="flex flex-col el-gap-field">
      <div>
        <p className="text-sm font-semibold text-foreground">GST rate-wise ledger</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Creates a dedicated ledger for one GST rate (e.g. &quot;CGST Output @9%&quot;) — a sibling of the
          consolidated ledger, and the same one invoices at that rate will auto-resolve to.
        </p>
      </div>
      <GstComponentFields formData={formData} setFormData={setFormData} />
      <GstRateField formData={formData} setFormData={setFormData} />
      <GstResolutionPreview formData={formData} resolution={resolution} />
    </div>
  )
}

/** Whether the GST step has everything it needs to create the ledger. */
export function canProceedGstStep(formData: AccountFormData, accountsMap: Map<string, Account>): boolean {
  const { consolidatedAccount, duplicateAccount, hasValidRate } = resolveGst(formData, accountsMap)
  return !!(formData.gstComponent && formData.gstDirection && hasValidRate && consolidatedAccount && !duplicateAccount && formData.code && formData.parentId)
}
